import { useState } from "react";
import { Check, AlertTriangle, AlertCircle, Pencil } from "lucide-react";
import type { Confidence } from "@/types/extraction";

interface ExtractedFieldRowProps {
  label: string;
  value: string | number | null;
  displayValue?: string;  // e.g., "$1,842 / six-month" instead of raw number
  confidence: Confidence;
  sourceQuote: string | null;
  acknowledged: boolean;
  onAcknowledge: () => void;
  onEdit: (newValue: string) => void;
  editType?: "text" | "number";
}

export function ExtractedFieldRow({
  label,
  value,
  displayValue,
  confidence,
  sourceQuote,
  acknowledged,
  onAcknowledge,
  onEdit,
  editType = "text",
}: ExtractedFieldRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value === null ? "" : String(value));

  const needsConfirmation =
    (confidence === "medium" || confidence === "low") && !acknowledged;

  const style = getRowStyle(confidence, acknowledged, needsConfirmation);

  const handleSave = () => {
    onEdit(editValue);
    setIsEditing(false);
  };

  return (
    <div style={style.row}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <ConfidenceIcon confidence={confidence} acknowledged={acknowledged} />
            <div
              className="text-xs uppercase tracking-wide font-semibold"
              style={{ color: style.labelColor, letterSpacing: 0.4 }}
            >
              {label}
            </div>
          </div>

          {isEditing ? (
            <div className="flex gap-2 mt-2">
              <input
                type={editType}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                className="form-input"
                style={{ flex: 1 }}
              />
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                style={{ padding: "8px 16px" }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditValue(value === null ? "" : String(value));
                  setIsEditing(false);
                }}
                style={{
                  padding: "8px 16px",
                  background: "white",
                  border: "1px solid var(--border-default, #E5E7EB)",
                  borderRadius: 12,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div
              className="font-medium"
              style={{ color: "var(--color-navy)", fontSize: 16 }}
            >
              {displayValue ?? (value === null ? "—" : String(value))}
            </div>
          )}

          {sourceQuote && (confidence === "low" || confidence === "medium") && !isEditing && (
            <div
              className="text-xs mt-2 italic"
              style={{ color: "var(--text-secondary)" }}
            >
              From your policy: &ldquo;{sourceQuote}&rdquo;
            </div>
          )}
        </div>

        {!isEditing && (
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            {needsConfirmation && (
              <button
                type="button"
                onClick={onAcknowledge}
                style={{
                  background: confidence === "low" ? "#DC2626" : "#D97706",
                  color: "white",
                  fontSize: 12,
                  fontWeight: 600,
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {confidence === "low" ? "Looks right" : "Confirm"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              aria-label={`Edit ${label}`}
              style={{
                background: "transparent",
                border: "none",
                color: "var(--text-secondary)",
                cursor: "pointer",
                padding: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 13,
              }}
            >
              <Pencil size={14} strokeWidth={2} />
              Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ConfidenceIcon({
  confidence,
  acknowledged,
}: {
  confidence: Confidence;
  acknowledged: boolean;
}) {
  if (confidence === "not_found") return null;
  if (confidence === "high" || acknowledged) {
    return (
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#16A34A",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          flexShrink: 0,
        }}
      >
        <Check size={12} strokeWidth={3} />
      </div>
    );
  }
  if (confidence === "medium") {
    return (
      <AlertTriangle
        size={18}
        strokeWidth={2}
        style={{ color: "#D97706", flexShrink: 0 }}
      />
    );
  }
  // low
  return (
    <AlertCircle
      size={18}
      strokeWidth={2}
      style={{ color: "#DC2626", flexShrink: 0 }}
    />
  );
}

function getRowStyle(
  confidence: Confidence,
  acknowledged: boolean,
  needsConfirmation: boolean,
): { row: React.CSSProperties; labelColor: string } {
  if (confidence === "high" || acknowledged) {
    return {
      row: {
        padding: "14px 16px",
        borderBottom: "1px solid var(--border-subtle, #F3F4F6)",
        background: "white",
      },
      labelColor: "var(--text-secondary)",
    };
  }
  if (needsConfirmation && confidence === "medium") {
    return {
      row: {
        padding: "14px 16px",
        borderBottom: "1px solid #FDE68A",
        background: "#FFFBEB",
      },
      labelColor: "#92400E",
    };
  }
  if (needsConfirmation && confidence === "low") {
    return {
      row: {
        padding: "14px 16px",
        borderBottom: "1px solid #FECACA",
        background: "#FEF2F2",
      },
      labelColor: "#991B1B",
    };
  }
  return {
    row: {
      padding: "14px 16px",
      borderBottom: "1px solid var(--border-subtle, #F3F4F6)",
      background: "white",
    },
    labelColor: "var(--text-secondary)",
  };
}
